// Two SA-only widgets that compose into SAOrgDetail.jsx:
//   1. <SpendCard> — lifetime paid amount + count + recent transactions list.
//   2. <DataCleanupCard> — scoped destructive ops with confirmation flow.
//      Attachments + Transactions open itemized managers (list + selective
//      delete); Activity/Notifications remain bulk-delete with a count preview.
//
// Backend: /superadmin/organizations/:orgId/{spend,cleanup,cleanup-counts,
//          attachments,attachments/delete,transactions-list,transactions/delete}

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  IndianRupee, Receipt, Loader2, Trash2, AlertTriangle,
  Activity, Bell, Paperclip, HardDrive, History,
} from 'lucide-react';

function fmtINR(rupees) {
  if (rupees == null) return '₹0';
  return `₹${Number(rupees).toLocaleString('en-IN')}`;
}
function fmtBytes(b) {
  if (!b || b <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

const CLEANUP_ACTION_LABELS = {
  'sa.org.cleanup.transactions': 'Deleted all transactions',
  'sa.org.cleanup.transactions_selective': 'Deleted transactions',
  'sa.org.cleanup.activity': 'Deleted activity & logs',
  'sa.org.cleanup.notifications': 'Deleted notifications',
  'sa.org.cleanup.attachments': 'Deleted all attachments',
  'sa.org.cleanup.attachments_selective': 'Deleted attachments',
  'sa.org.cleanup.full_wipe': 'Full data wipe',
  'sa.storage.recompute': 'Recomputed storage',
};
function summarizeAudit(a) {
  const d = a.details || {};
  if (a.action === 'sa.org.cleanup.attachments_selective') {
    return `${d.deleted || 0} file(s)${d.freed_bytes ? ` · freed ${fmtBytes(d.freed_bytes)}` : ''}${d.all ? ' (all)' : ''}`;
  }
  if (a.action === 'sa.org.cleanup.transactions_selective') {
    return `${d.deleted || 0} deleted${d.skipped_paid ? ` · ${d.skipped_paid} paid protected` : ''}`;
  }
  if (a.action === 'sa.storage.recompute') {
    return `${fmtBytes(d.used_bytes)} · ${d.file_count ?? 0} files`;
  }
  if (d.summary && typeof d.summary === 'object') {
    const total = Object.values(d.summary).reduce((s, n) => s + (Number(n) || 0), 0);
    return `${total} record(s)`;
  }
  return '';
}

export function SpendCard({ apiFetch, orgId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/organizations/${orgId}/spend`);
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, [apiFetch, orgId]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="border-slate-200/60" data-testid="sa-spend-card">
        <CardContent className="py-6 flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading spend…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;
  const recent = data.recent_transactions || [];

  return (
    <Card className="border-emerald-200/70 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="sa-spend-card">
      <CardContent className="py-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <IndianRupee className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-emerald-700/80">
                Total Spend (Lifetime)
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-0.5" data-testid="sa-total-spend-value">
                {fmtINR(data.total_amount_rupees)}
              </p>
              {data.first_paid_at && (
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Since {new Date(data.first_paid_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-600">
                Paid Transactions
              </p>
              <p className="text-2xl font-bold text-slate-900 mt-0.5" data-testid="sa-paid-count-value">
                {data.paid_count}
              </p>
              {data.last_paid_at && (
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Last on {new Date(data.last_paid_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-600 mb-1.5">
              Recent Transactions
            </p>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {recent.length === 0 && <p className="text-[11px] text-slate-400">No transactions yet</p>}
              {recent.slice(0, 4).map(t => (
                <div key={t.transaction_id} className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-600 truncate">
                    {fmtINR(Math.round((t.amount || 0) / 100))} · {t.desired_seats || '—'} seat
                    {t.desired_seats === 1 ? '' : 's'}
                  </span>
                  <Badge className={`text-[9px] ${t.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
                    {t.status === 'initiated' ? 'pending' : t.status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Attachment manager: itemized, largest-first, selective delete ──────────
function AttachmentManagerDialog({ apiFetch, orgId, orgName, open, onClose, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/attachments`);
      if (res.ok) setData(await res.json());
      setSelected(new Set());
    } finally { setLoading(false); }
  }, [apiFetch, orgId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const items = data?.items || [];
  const toggle = (att) => {
    const key = `${att.collection}:${att.attachment_id}`;
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map(a => `${a.collection}:${a.attachment_id}`)));
  };
  const selectedBytes = items
    .filter(a => selected.has(`${a.collection}:${a.attachment_id}`))
    .reduce((s, a) => s + (a.size_bytes || 0), 0);

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} file${selected.size === 1 ? '' : 's'} (${fmtBytes(selectedBytes)})?\nThis frees storage and cannot be undone.`)) return;
    setBusy(true);
    try {
      const payload = items
        .filter(a => selected.has(`${a.collection}:${a.attachment_id}`))
        .map(a => ({ collection: a.collection, attachment_id: a.attachment_id }));
      const res = await apiFetch(`/organizations/${orgId}/attachments/delete`, { method: 'POST', body: JSON.stringify({ items: payload }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Delete failed'); return; }
      toast.success(`Deleted ${d.deleted_count} file(s) · freed ${fmtBytes(d.freed_bytes)}`);
      await load();
      if (onChanged) onChanged();
    } finally { setBusy(false); }
  };

  const deleteAll = async () => {
    if (!window.confirm(`Delete ALL ${items.length} attachments for ${orgName}?\nThis frees all attachment storage and cannot be undone.`)) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/attachments/delete`, { method: 'POST', body: JSON.stringify({ all: true }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Delete failed'); return; }
      toast.success(`Deleted ${d.deleted_count} file(s) · freed ${fmtBytes(d.freed_bytes)}`);
      await load();
      if (onChanged) onChanged();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="sa-attachment-manager">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm"><Paperclip className="w-4 h-4" /> Attachments — {orgName}</DialogTitle>
          <DialogDescription className="text-[11px]">
            Delete the biggest files to free storage. The org's usage counter updates instantly.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading files…</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-slate-600 border-b border-slate-100 pb-2.5">
              <span className="flex items-center gap-1.5"><HardDrive className="w-3.5 h-3.5 text-slate-400" /> Counter: <strong className="text-slate-900" data-testid="sa-attach-counter">{fmtBytes(data?.storage_counter?.used_bytes)}</strong></span>
              <span>Files: <strong className="text-slate-900">{data?.total_count ?? 0}</strong></span>
              <span>Total: <strong className="text-slate-900">{fmtBytes(data?.total_bytes)}</strong></span>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="sa-attach-select-all" />
                Select all
              </label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="destructive" disabled={busy || selected.size === 0} onClick={deleteSelected} className="bg-red-600 hover:bg-red-700 h-7 text-[11px]" data-testid="sa-attach-delete-selected">
                  {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                  Delete selected{selected.size > 0 ? ` (${selected.size} · ${fmtBytes(selectedBytes)})` : ''}
                </Button>
                <Button size="sm" variant="outline" disabled={busy || items.length === 0} onClick={deleteAll} className="h-7 text-[11px]" data-testid="sa-attach-delete-all">
                  Delete all
                </Button>
              </div>
            </div>

            <div className="max-h-[46vh] overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
              {items.length === 0 && <p className="text-[11px] text-slate-400 py-8 text-center">No attachments</p>}
              {items.map(a => {
                const key = `${a.collection}:${a.attachment_id}`;
                return (
                  <div key={key} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`sa-attach-row-${a.attachment_id}`}>
                    <Checkbox checked={selected.has(key)} onCheckedChange={() => toggle(a)} data-testid={`sa-attach-check-${a.attachment_id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-900 truncate">{a.filename || '(unnamed)'}</p>
                      <p className="text-[10px] text-slate-400">{a.source} · {a.mime_type || 'file'} · {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</p>
                    </div>
                    <span className="text-[11px] font-semibold text-slate-700 tabular-nums flex-shrink-0">{fmtBytes(a.size_bytes)}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Transaction manager: list + selective delete (paid = protected) ────────
function TransactionManagerDialog({ apiFetch, orgId, orgName, open, onClose, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/transactions-list`);
      if (res.ok) setData(await res.json());
      setSelected(new Set());
    } finally { setLoading(false); }
  }, [apiFetch, orgId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const items = data?.items || [];
  const deletableItems = items.filter(t => t.deletable);
  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSelected = deletableItems.length > 0 && selected.size === deletableItems.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(deletableItems.map(t => t.transaction_id)));
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} transaction(s)? Paid records are always protected.`)) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/transactions/delete`, { method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Delete failed'); return; }
      toast.success(`Deleted ${d.deleted_count} transaction(s)${d.skipped_paid ? ` · ${d.skipped_paid} paid skipped` : ''}`);
      await load();
      if (onChanged) onChanged();
    } finally { setBusy(false); }
  };

  const deleteAllDeletable = async () => {
    if (deletableItems.length === 0) return;
    if (!window.confirm(`Delete all ${deletableItems.length} non-paid transaction(s) for ${orgName}? Paid records stay.`)) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/organizations/${orgId}/transactions/delete`, { method: 'POST', body: JSON.stringify({ all_deletable: true }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Delete failed'); return; }
      toast.success(`Deleted ${d.deleted_count} transaction(s)`);
      await load();
      if (onChanged) onChanged();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="sa-transaction-manager">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm"><Receipt className="w-4 h-4" /> Transactions — {orgName}</DialogTitle>
          <DialogDescription className="text-[11px]">
            Remove stale <strong>pending / failed</strong> payment records. Paid transactions are protected for accounting.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center gap-2 text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="flex items-center justify-between py-1">
              <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} disabled={deletableItems.length === 0} data-testid="sa-txn-select-all" />
                Select all deletable ({deletableItems.length})
              </label>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="destructive" disabled={busy || selected.size === 0} onClick={deleteSelected} className="bg-red-600 hover:bg-red-700 h-7 text-[11px]" data-testid="sa-txn-delete-selected">
                  {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                  Delete selected{selected.size > 0 ? ` (${selected.size})` : ''}
                </Button>
                <Button size="sm" variant="outline" disabled={busy || deletableItems.length === 0} onClick={deleteAllDeletable} className="h-7 text-[11px]" data-testid="sa-txn-delete-all">
                  Delete all non-paid
                </Button>
              </div>
            </div>

            <div className="max-h-[46vh] overflow-y-auto border border-slate-100 rounded-lg divide-y divide-slate-50">
              {items.length === 0 && <p className="text-[11px] text-slate-400 py-8 text-center">No transactions</p>}
              {items.map(t => (
                <div key={t.transaction_id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`sa-txn-row-${t.transaction_id}`}>
                  <Checkbox checked={selected.has(t.transaction_id)} disabled={!t.deletable} onCheckedChange={() => toggle(t.transaction_id)} data-testid={`sa-txn-check-${t.transaction_id}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-900">{fmtINR(Math.round((t.amount || 0) / 100))} · {t.desired_seats || '—'} seat{t.desired_seats === 1 ? '' : 's'}</p>
                    <p className="text-[10px] text-slate-400 truncate">{t.provider || 'razorpay'} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</p>
                  </div>
                  {t.deletable ? (
                    <Badge className="text-[9px] bg-zinc-100 text-zinc-600">{t.status === 'initiated' ? 'pending' : t.status}</Badge>
                  ) : (
                    <Badge className="text-[9px] bg-emerald-100 text-emerald-700">Paid · protected</Badge>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function DataCleanupCard({ apiFetch, orgId, orgSlug, orgName, onWiped }) {
  const [busy, setBusy] = useState(null);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [counts, setCounts] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [txnOpen, setTxnOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const loadCounts = useCallback(async () => {
    try {
      const res = await apiFetch(`/organizations/${orgId}/cleanup-counts`);
      if (res.ok) setCounts(await res.json());
    } catch (_) { /* non-blocking */ }
  }, [apiFetch, orgId]);
  const loadAudit = useCallback(async () => {
    try {
      const res = await apiFetch(`/organizations/${orgId}/cleanup-audit`);
      if (res.ok) { const d = await res.json(); setAudit(d.items || []); }
    } catch (_) { /* non-blocking */ }
  }, [apiFetch, orgId]);
  const refreshAll = useCallback(() => { loadCounts(); loadAudit(); }, [loadCounts, loadAudit]);
  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Bulk delete for high-volume, low-value records (activity, notifications).
  const runBulkScope = async (scope, count) => {
    const label = scope === 'activity' ? 'activity & audit logs' : 'notifications';
    if (!window.confirm(`Permanently delete ${count != null ? count.toLocaleString() : 'all'} ${label} for ${orgName}?\n\nThis cannot be undone.`)) return;
    setBusy(scope);
    try {
      const res = await apiFetch(`/organizations/${orgId}/cleanup`, { method: 'POST', body: JSON.stringify({ scope }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || `Cleanup of ${scope} failed`); return; }
      const total = Object.values(data.summary || {}).reduce((s, n) => s + n, 0);
      toast.success(`Removed ${total} record${total === 1 ? '' : 's'}`);
      refreshAll();
    } catch (err) {
      toast.error('Cleanup request failed');
    } finally { setBusy(null); }
  };

  const runFullWipe = async () => {
    if (wipeConfirm.trim().toLowerCase() !== (orgSlug || '').toLowerCase()) {
      toast.error(`Type the org slug "${orgSlug}" exactly to confirm`);
      return;
    }
    setBusy('full_wipe');
    try {
      const res = await apiFetch(`/organizations/${orgId}/cleanup`, {
        method: 'POST', body: JSON.stringify({ scope: 'full_wipe', confirm: wipeConfirm.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Full wipe failed'); return; }
      const total = Object.values(data.summary || {}).reduce((s, n) => s + n, 0);
      toast.success(`Org wiped — removed ${total} record${total === 1 ? '' : 's'} across ${Object.keys(data.summary || {}).length} collections`);
      setWipeOpen(false);
      setWipeConfirm('');
      if (onWiped) onWiped();
    } catch (err) {
      toast.error('Full wipe request failed');
    } finally { setBusy(null); }
  };

  const Tile = ({ id, icon: Icon, label, desc, count, onClick }) => (
    <button
      onClick={onClick}
      disabled={!!busy}
      className="text-left rounded-xl border border-slate-200 bg-white px-3 py-3 hover:border-red-300 hover:bg-red-50/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid={`sa-cleanup-${id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-red-600" />
        <span className="text-xs font-semibold text-slate-900">{label}</span>
        {busy === id && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-auto" />}
        {count != null && busy !== id && (
          <span className="ml-auto text-[10px] font-bold text-slate-500 tabular-nums">{count.toLocaleString()}</span>
        )}
      </div>
      <p className="text-[10px] text-slate-500 leading-snug">{desc}</p>
    </button>
  );

  return (
    <Card className="border-red-200/70 bg-red-50/30" data-testid="sa-cleanup-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2 text-red-700">
          <Trash2 className="w-4 h-4" /> Data Cleanup
        </CardTitle>
        <p className="text-[11px] text-red-600/80 mt-1">
          Manage or permanently delete data for <strong>{orgName}</strong>. This cannot be undone.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Tile id="transactions" icon={Receipt} label="Transactions" desc="Review & delete pending/failed records" count={counts?.transactions} onClick={() => setTxnOpen(true)} />
          <Tile id="activity" icon={Activity} label="Activity & Logs" desc="Bulk delete task activity + audit logs" count={counts?.activity} onClick={() => runBulkScope('activity', counts?.activity)} />
          <Tile id="notifications" icon={Bell} label="Notifications" desc="Bulk delete in-app notifications" count={counts?.notifications} onClick={() => runBulkScope('notifications', counts?.notifications)} />
          <Tile id="attachments" icon={Paperclip} label="Attachments" desc="Browse files by size & free storage" count={counts?.attachments} onClick={() => setAttachOpen(true)} />

          {/* FULL WIPE — destructive, isolated tile with own confirm flow */}
          <button
            onClick={() => setWipeOpen(true)}
            disabled={!!busy}
            className="text-left rounded-xl border-2 border-red-300 bg-red-100/50 px-3 py-3 hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="sa-cleanup-full-wipe"
          >
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-3.5 h-3.5 text-red-700" />
              <span className="text-xs font-bold text-red-700 uppercase tracking-wide">Full Wipe</span>
              {busy === 'full_wipe' && <Loader2 className="w-3 h-3 animate-spin text-red-500 ml-auto" />}
            </div>
            <p className="text-[10px] text-red-600/90 leading-snug">Delete ALL data for this org (irreversible)</p>
          </button>
        </div>

        {/* Full-wipe confirmation row */}
        {wipeOpen && (
          <div className="mt-4 rounded-xl border-2 border-red-300 bg-white p-4" data-testid="sa-full-wipe-confirm">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-red-700">
                  This will permanently delete tasks, projects, sprints, members, integrations, payment history, and storage for <strong>{orgName}</strong>.
                </p>
                <p className="text-[11px] text-slate-600 mt-1">
                  The organization row will be marked <em>Suspended</em> for audit purposes.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="wipe-confirm" className="text-[11px] text-slate-700">
                Type <code className="px-1 py-0.5 rounded bg-slate-100 text-red-700 font-mono">{orgSlug}</code> to confirm:
              </Label>
              <Input
                id="wipe-confirm"
                value={wipeConfirm}
                onChange={e => setWipeConfirm(e.target.value)}
                placeholder={orgSlug}
                className="font-mono text-xs"
                data-testid="sa-full-wipe-confirm-input"
              />
              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={runFullWipe}
                  disabled={busy === 'full_wipe' || wipeConfirm.trim().toLowerCase() !== (orgSlug || '').toLowerCase()}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="sa-full-wipe-confirm-btn"
                >
                  {busy === 'full_wipe'
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Wiping…</>
                    : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Permanently wipe org data</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setWipeOpen(false); setWipeConfirm(''); }}
                  disabled={busy === 'full_wipe'}
                  data-testid="sa-full-wipe-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Recent cleanup activity — small audit trail for accountability. */}
        <div className="mt-4 pt-3 border-t border-red-200/50" data-testid="sa-cleanup-audit">
          <div className="flex items-center gap-1.5 mb-2">
            <History className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">Recent cleanup activity</span>
          </div>
          {audit.length === 0 ? (
            <p className="text-[11px] text-slate-400">No cleanup actions yet.</p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {audit.map(a => (
                <div key={a.audit_id} className="flex items-center gap-2 text-[11px] py-1 border-b border-slate-50 last:border-0" data-testid={`sa-audit-row-${a.audit_id}`}>
                  <span className="font-semibold text-slate-800 flex-shrink-0">{CLEANUP_ACTION_LABELS[a.action] || a.action}</span>
                  {summarizeAudit(a) && <span className="text-slate-500 truncate">· {summarizeAudit(a)}</span>}
                  <span className="ml-auto text-slate-400 flex-shrink-0 text-[10px]">
                    {a.super_admin_email ? `${a.super_admin_email} · ` : ''}{a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      <AttachmentManagerDialog apiFetch={apiFetch} orgId={orgId} orgName={orgName} open={attachOpen} onClose={() => setAttachOpen(false)} onChanged={refreshAll} />
      <TransactionManagerDialog apiFetch={apiFetch} orgId={orgId} orgName={orgName} open={txnOpen} onClose={() => setTxnOpen(false)} onChanged={refreshAll} />
    </Card>
  );
}
